import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Security as SecurityIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { getSubscriptionInfo } from '../services/api';
import CertificateWizard from './CertificateWizard';

interface AccountSettingsProps {
  tenantId?: string;
}

export default function AccountSettings({ tenantId }: AccountSettingsProps) {
  const [subscription, setSubscription] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [hasCertificate, setHasCertificate] = useState(false);

  // Debug logging
  console.log('AccountSettings rendered with tenantId:', tenantId);

  useEffect(() => {
    console.log('AccountSettings useEffect triggered, tenantId:', tenantId);
    if (tenantId) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [tenantId]);

  const loadData = async () => {
    if (!tenantId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const subData = await getSubscriptionInfo(tenantId);
      
      setSubscription(subData);
      
      // Check if customer has certificate configured (from Customers table)
      setHasCertificate(
        subData.CertificateThumbprint !== null && subData.CertificateThumbprint !== ''
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  const handleWizardOpen = () => {
    setWizardOpen(true);
  };

  const handleWizardClose = () => {
    setWizardOpen(false);
    loadData(); // Reload data after wizard completion
  };

  const formatDate = (dateValue?: string) => {
    if (!dateValue) return 'N/A';
    return new Date(dateValue).toLocaleDateString();
  };

  const isDateExpired = (dateValue?: string) => {
    if (!dateValue) return false;
    return new Date(dateValue) < new Date();
  };

  const marketplaceTermEndDate: string | undefined = subscription?.MarketplaceTermEndDate;
  const marketplaceTermExpired = isDateExpired(marketplaceTermEndDate);

  const hasAttachedEnterpriseLicense =
    !!subscription?.EnterpriseClientLicense && subscription.EnterpriseClientLicense.trim().length > 0;
  const enterpriseLicenseEndDate: string | undefined = subscription?.EnterpriseClientLicenseEndDate;
  const enterpriseLicenseExpired =
    subscription?.EnterpriseClientLicenseExpired ??
    (hasAttachedEnterpriseLicense && isDateExpired(enterpriseLicenseEndDate));

  const lifecycleState: string = subscription?.LifecycleState ?? 'Active';
  const isLifecycleLocked: boolean = subscription?.IsLifecycleLocked === true;
  const dataRetentionUntil: string | undefined = subscription?.DataRetentionUntil;

  if (!tenantId) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Alert severity="warning">
            No customer ID available. Please sign in.
          </Alert>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Account Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Entitlement Information */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {isLifecycleLocked && (
              <Alert
                severity={lifecycleState === 'Unsubscribed' ? 'error' : 'warning'}
                sx={{ mb: 3 }}
              >
                {lifecycleState === 'Unsubscribed'
                  ? `Your subscription has been cancelled. Your data is in read-only mode until ${dataRetentionUntil ? new Date(dataRetentionUntil).toLocaleDateString() : 'pending'}. After this date all account data will be permanently deleted.`
                  : 'Your subscription is currently suspended. Access is read-only. Resolve the billing issue in your Azure Marketplace account to restore full access.'}
              </Alert>
            )}

            {/* Entitlement Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5">Entitlement Information</Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {subscription && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Company Name
                      </TableCell>
                      <TableCell>{subscription.CompanyName}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Tenant ID
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {subscription.TenantID || 'Not configured'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Entitlement Type
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={subscription.EntitlementType || 'Base'}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Status
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={subscription.SubscriptionStatus}
                          color={subscription.SubscriptionStatus === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Lifecycle State
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip
                            label={lifecycleState}
                            color={
                              lifecycleState === 'Active' ? 'success'
                              : lifecycleState === 'Suspended' ? 'warning'
                              : lifecycleState === 'Unsubscribed' ? 'error'
                              : 'default'
                            }
                            size="small"
                          />
                          {dataRetentionUntil && lifecycleState !== 'Active' && (
                            <Typography variant="caption" color="text.secondary">
                              Data retained until {new Date(dataRetentionUntil).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Monthly Fee
                      </TableCell>
                      <TableCell>${subscription.MonthlyFee?.toFixed(2) || '0.00'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Onboarding Date
                      </TableCell>
                      <TableCell>
                        {subscription.OnboardingDate
                          ? new Date(subscription.OnboardingDate).toLocaleDateString()
                          : 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Next Billing Date
                      </TableCell>
                      <TableCell>
                        {formatDate(subscription.NextBillingDate)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Marketplace Term End
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="body2">{formatDate(marketplaceTermEndDate)}</Typography>
                          {marketplaceTermEndDate && (
                            <Chip
                              label={marketplaceTermExpired ? 'Expired' : 'Active'}
                              color={marketplaceTermExpired ? 'error' : 'success'}
                              size="small"
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Enterprise Attached License
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip
                            label={hasAttachedEnterpriseLicense ? 'Attached' : 'Not Attached'}
                            color={hasAttachedEnterpriseLicense ? 'info' : 'default'}
                            size="small"
                            variant={hasAttachedEnterpriseLicense ? 'filled' : 'outlined'}
                          />
                          {hasAttachedEnterpriseLicense && (
                            <>
                              <Typography variant="body2">
                                End: {formatDate(enterpriseLicenseEndDate)}
                              </Typography>
                              <Chip
                                label={enterpriseLicenseExpired ? 'Expired' : 'Active'}
                                color={enterpriseLicenseExpired ? 'error' : 'success'}
                                size="small"
                              />
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Plan Type
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={subscription.MarketplacePlanId || 'N/A'}
                          color="info"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Certificate Management */}
      <Paper id="certificate-management" sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5">Certificate Management</Typography>
          </Box>
          {hasCertificate && subscription?.CertificateThumbprint && (
            <Button
              variant="outlined"
              startIcon={<VpnKeyIcon />}
              onClick={handleWizardOpen}
            >
              Renew Certificate
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 3 }} />

        {!hasCertificate ? (
          <Box>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Certificate Setup Required
              </Typography>
              Your account needs a certificate configured to deploy policies and manage your Microsoft 365 tenant. 
              Click the button below to start the guided certificate setup wizard.
            </Alert>

            <Box textAlign="center" py={4}>
              <Typography color="text.secondary" paragraph>
                No certificate configured yet. Use the wizard to generate and configure your certificate.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<VpnKeyIcon />}
                onClick={handleWizardOpen}
              >
                Start Certificate Wizard
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              Certificate is configured and ready for deployments.
            </Alert>

            {subscription?.CertificateThumbprint && (
              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '200px' }}>
                        Thumbprint
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {subscription.CertificateThumbprint}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        App Registration ID
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {subscription.AppRegistrationID || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Expiry Date
                      </TableCell>
                      <TableCell>
                        {subscription.CertificateExpiryDate
                          ? new Date(subscription.CertificateExpiryDate).toLocaleDateString()
                          : 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        Status
                      </TableCell>
                      <TableCell>
                        {subscription.CertificateExpiryDate && 
                         new Date(subscription.CertificateExpiryDate) > new Date() ? (
                          <Chip label="Valid" color="success" size="small" icon={<CheckIcon />} />
                        ) : (
                          <Chip label="Expired" color="error" size="small" icon={<ErrorIcon />} />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

      </Paper>

      {/* Certificate Wizard Dialog */}
      <Dialog open={wizardOpen} onClose={handleWizardClose} maxWidth="md" fullWidth>
        <DialogTitle>Certificate Setup Wizard</DialogTitle>
        <DialogContent>
          {subscription?.TenantID && (
            <CertificateWizard
              tenantId={subscription.TenantID}
              tenantDomain={subscription.TenantID}
              onComplete={handleWizardClose}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
